import { fetchData } from './client';

export type MemberStatus = 'NORMAL' | 'BRONZE' | 'SILVER' | 'GOLD';

export type BalanceResponse = {
  balance: number;
  asOf: string;
  memberStatus: MemberStatus;
};

export const getBalance = async (): Promise<BalanceResponse> => {
  const response = await fetchData('wallet/balance', {
    method: 'GET',
  });

  if (!response.ok) {
    throw new Error('残高の取得に失敗しました');
  }

  const result: BalanceResponse = await response.json();
  return result;
};

export interface TransactionHistoryItem {
  code: string;
  type: '00' | '01' | '02'; // 00: チャージ, 01: 支払い, 02: 送金
  title: string;
  transactionDate: string;
  amount: number;
}

export interface TransactionHistoryResponse {
  transactions: TransactionHistoryItem[];
}

export const getTransactionHistory = async (): Promise<TransactionHistoryResponse> => {
  const response = await fetchData('wallet/history', {
    method: 'GET',
  });

  if (!response.ok) {
    throw new Error('決済履歴の取得に失敗しました');
  }

  const result: TransactionHistoryResponse = await response.json();
  return result;
};

export interface QRCodeResponse {
  qrCodeUrl: string;
}

export const getQRCode = async (): Promise<QRCodeResponse> => {
  const response = await fetchData('wallet/qrcode', {
    method: 'POST',
  });

  if (!response.ok) {
    throw new Error('QRコードの取得に失敗しました');
  }

  const result: QRCodeResponse = await response.json();
  return result;
};

export interface ChargeRequest {
  chargeCode: string;
}

export interface ChargeResponse {
  amount: number;
  balanceAfter: number;
  transactionCode: string;
  chargedAt: string;
}

export const chargeBalance = async (request: ChargeRequest): Promise<ChargeResponse> => {
  const response = await fetchData('wallet/charge', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    const message = errorData?.message || 'チャージに失敗しました';
    throw new Error(message);
  }

  const result: ChargeResponse = await response.json();
  return result;
};

export interface ParseQRRequest {
  encodedValue: string;
}

export interface ParseQRResponse {
  type: 'STORE' | 'PRODUCT';
  qrId: string;
  timestamp: number;
  issuedAt: string;
  payeeName: string;
  payeeDetail: string | null;
  amount: number | null;
}

export const parseQRCode = async (request: ParseQRRequest): Promise<ParseQRResponse> => {
  const response = await fetchData('wallet/parse-qr', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    const message = errorData?.message || 'QRコードの解析に失敗しました';
    throw new Error(message);
  }

  const result: ParseQRResponse = await response.json();
  return result;
};

export interface PaymentItem {
  productCode: string;
  quantity: number;
}

export interface PaymentRequest {
  encodedValue: string;
  amount?: number;
  items?: PaymentItem[];
}

export interface PaymentResponse {
  transactionCode: string;
  payeeName: string;
  payeeDetail: string | null;
  amount: number;
  balanceAfter: number;
  timestamp: string;
}

export interface StoreProductsApiProduct {
  productCode: string;
  productName: string;
  amount: number;
  hasFixedAmount: boolean;
  category: string | null;
  /** 00: 販売中, 01: 一時休止, 02: 入荷未定 */
  productStatus: '00' | '01' | '02';
}

export interface StoreProductsResponse {
  storeName: string;
  products: StoreProductsApiProduct[];
}

export const getStoreProducts = async (encodedValue: string): Promise<StoreProductsResponse> => {
  const params = new URLSearchParams({ encodedValue });
  const response = await fetchData(`wallet/store-products?${params.toString()}`, {
    method: 'GET',
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    const message = errorData?.message || '商品一覧の取得に失敗しました';
    throw new Error(message);
  }

  const result: StoreProductsResponse = await response.json();
  return result;
};

// 取引詳細
export interface TransactionDetailItem {
  productCode: string;
  productName: string;
  unitPrice: number;
  quantity: number;
}

export interface TransactionDetailResponse {
  transactionCode: string;
  transactionType: '00' | '01' | '02';
  title: string;
  storeCode: string | null;
  storeName: string | null;
  items: TransactionDetailItem[] | null;
  totalAmount: number;
  balanceAfter: number;
  transactionDate: string;
}

export const getTransactionDetail = async (
  transactionCode: string
): Promise<TransactionDetailResponse> => {
  const response = await fetchData(`wallet/history/${transactionCode}`, {
    method: 'GET',
  });

  if (!response.ok) {
    throw new Error('取引詳細の取得に失敗しました');
  }

  const result: TransactionDetailResponse = await response.json();
  return result;
};

export const executePayment = async (request: PaymentRequest): Promise<PaymentResponse> => {
  const response = await fetchData('wallet/pay', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    const message = errorData?.message || '支払いに失敗しました';
    throw new Error(message);
  }

  const result: PaymentResponse = await response.json();
  return result;
};
